import L from 'leaflet'
import type { GeoPoint } from '@lib/dto/geo.dto'
import { labelPoint } from '@/map/labelPoint'
import { regionLabelHtml, type RegionLabelProps } from '@/map/RegionLabel'

/**
 * The pane labels are drawn in: above the outlines (`overlayPane`, 400, and
 * the boundary page's areas and tree dots, 450 and 480) so a name is never
 * under its own edge, and below the pins (`markerPane`, 600) so it never
 * covers one somebody is trying to tap. Labels take no taps, which pass to
 * what is beneath.
 */
const PANE = 'swocrm-region-labels'
const PANE_Z_INDEX = '490'

/**
 * Over the markers instead, for a map where the markers are the dense part
 * and the names would otherwise be lost under them: the proposal's plan,
 * where every tree is a numbered circle. Still taking no taps, so a tree
 * under a name is picked as before.
 */
const TOP_PANE = 'swocrm-region-labels-top'
const TOP_PANE_Z_INDEX = '650'

/** Room kept clear between two labels, in screen pixels. */
const LABEL_GAP_PX = 4

export interface RegionLabelSpec {
  key: string
  /** The ring the label names, for where it goes and whether it fits. */
  ring: readonly GeoPoint[]
  label: RegionLabelProps
  /** Drawn whatever the room: the zone being walked. */
  always?: boolean
}

interface Placed {
  spec: RegionLabelSpec
  marker: L.Marker
  /** The ring's corners, to measure it on screen at each zoom. */
  bounds: L.LatLngBounds
}

/**
 * The names and numbers on a map's regions (docs/map-regions.md).
 *
 * Each is written at its ring's label point and hidden when it would not
 * help: when the ring is too small on screen for the words, or the words
 * would run into a label already placed. Bigger regions are placed first,
 * so when two collide the one with more room keeps its name. Worked out
 * again at every zoom, because both depend on it.
 */
export class RegionLabelLayer {
  private readonly map: L.Map

  private readonly group: L.LayerGroup

  private placed: Placed[] = []

  private readonly pane: string

  private readonly layoutOnZoom = (): void => {
    this.layout()
  }

  constructor(map: L.Map, options: { overMarkers?: boolean } = {}) {
    this.map = map
    this.pane = options.overMarkers === true ? TOP_PANE : PANE
    if (map.getPane(this.pane) === undefined) {
      const pane = map.createPane(this.pane)
      pane.style.zIndex =
        options.overMarkers === true ? TOP_PANE_Z_INDEX : PANE_Z_INDEX
      pane.style.pointerEvents = 'none'
    }
    this.group = L.layerGroup().addTo(map)
    map.on('zoomend', this.layoutOnZoom)
  }

  /** Replace every label with these. */
  set(specs: readonly RegionLabelSpec[]): void {
    this.group.clearLayers()
    this.placed = specs.flatMap((spec) => {
      if (spec.ring.length < 3) return []
      const at = labelPoint(spec.ring)
      const marker = L.marker(L.latLng(at.latitude, at.longitude), {
        pane: this.pane,
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          html: regionLabelHtml(spec.label),
          className: '',
          // The label centers itself on the point; the icon is only an anchor.
          iconSize: [0, 0],
          iconAnchor: [0, 0],
        }),
      }).addTo(this.group)
      const bounds = L.latLngBounds(
        spec.ring.map((point) => L.latLng(point.latitude, point.longitude)),
      )
      return [{ spec, marker, bounds }]
    })
    this.layout()
  }

  remove(): void {
    this.map.off('zoomend', this.layoutOnZoom)
    this.group.remove()
    this.placed = []
  }

  private layout(): void {
    const zoom = this.map.getZoom()
    const span = (entry: Placed) => {
      const corner = this.map.project(entry.bounds.getNorthWest(), zoom)
      const opposite = this.map.project(entry.bounds.getSouthEast(), zoom)
      return {
        width: Math.abs(opposite.x - corner.x),
        height: Math.abs(opposite.y - corner.y),
      }
    }
    const order = [...this.placed]
      .map((entry) => ({ entry, size: span(entry) }))
      .sort(
        (a, b) =>
          Number(b.entry.spec.always === true) -
            Number(a.entry.spec.always === true) ||
          b.size.width * b.size.height - a.size.width * a.size.height,
      )
    const taken: DOMRect[] = []
    for (const { entry, size } of order) {
      const text = entry.marker
        .getElement()
        ?.querySelector<HTMLElement>('[data-region-label]')
      if (text === null || text === undefined) continue
      // Shown first, so a label hidden at the last zoom can be measured.
      text.style.visibility = ''
      const box = text.getBoundingClientRect()
      const fits =
        size.width >= box.width * 0.75 && size.height >= box.height * 1.2
      const clear = !taken.some(
        (other) =>
          box.left < other.right + LABEL_GAP_PX &&
          other.left < box.right + LABEL_GAP_PX &&
          box.top < other.bottom + LABEL_GAP_PX &&
          other.top < box.bottom + LABEL_GAP_PX,
      )
      const show = entry.spec.always === true || (fits && clear)
      text.style.visibility = show ? '' : 'hidden'
      if (show) taken.push(box)
    }
  }
}
